import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import useUser from '../../auth/useUser';

const TODO_API = import.meta.env.VITE_API_URL;
const MI_API = import.meta.env.VITE_API_URL;

async function getAuthHeaders(msalInstance) {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return {};
    const resp = await msalInstance.acquireTokenSilent({
      scopes: [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`],
      account: accounts[0],
    });
    return { Authorization: `Bearer ${resp.idToken}` };
  } catch {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && accounts[0].idToken) {
      return { Authorization: `Bearer ${accounts[0].idToken}` };
    }
    return {};
  }
}

/* ── SVG icon components matching the mockup exactly ── */
const icons = {
  dashboard: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  building: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M3 21h18M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1" />
    </svg>
  ),
  stars: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l2.09 6.26L20.18 9l-5.09 3.74L16.18 19 12 15.27 7.82 19l1.09-6.26L3.82 9l6.09-.74z" />
    </svg>
  ),
  boxes: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM17.5 14v7M14 17.5h7" />
    </svg>
  ),
  products: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M4 6h16M4 10h16M4 14h9M4 18h6" />
    </svg>
  ),
  settings: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  clockHistory: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
      <path d="M3 12a9 9 0 009 9" strokeDasharray="4 2" />
    </svg>
  ),
  support: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M18 18.72a9.094 9.094 0 003.741-6.72A9 9 0 1012 21a8.966 8.966 0 006-.28" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  ),
  pulse: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M3 12h4l3-9 4 18 3-9h4" />
    </svg>
  ),
  atom: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="2" />
      <ellipse cx="12" cy="12" rx="9" ry="4" />
      <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(60 12 12)" />
      <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(120 12 12)" />
    </svg>
  ),
  pill: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M10.5 1.5l-8 8a4.95 4.95 0 007 7l8-8a4.95 4.95 0 00-7-7z" />
      <path d="M8.5 8.5l7 7" />
    </svg>
  ),
  trendingUp: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M23 6l-9.5 9.5-5-5L1 18" />
      <path d="M17 6h6v6" />
    </svg>
  ),
  sparkles: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 7.66l-.71-.71M4.05 4.05l-.71-.71" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  ),
  target: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  compass: (
    <svg className="si-ico" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  ),
};

/* ── Navigation structure ── */
const OVERVIEW_SECTION = {
  label: 'Overview',
  items: [
    { key: 'dashboard', path: '/', label: 'Dashboard', icon: 'dashboard' },
  ],
};

const SUPPLIER_CHILDREN = [
  { key: 'suppliers', path: '/suppliers', label: 'Suppliers', icon: 'building' },
  { key: 'supplier-products', path: '/supplier-products', label: 'Products', icon: 'products' },
  { key: 'supplier-contracts', path: '/supplier-contracts', label: 'Contracts', icon: 'products' },
  { key: 'delivery-delays', path: '/delivery-delays', label: 'Delivery Delays', icon: 'clockHistory' },
  { key: 'file-monitoring', path: '/file-monitoring', label: 'File Monitoring', icon: 'products' },
];

const ANALYTICS_CHILDREN = [
  { key: 'therapeutic-area-developments', path: '/therapeutic-area-developments', label: 'Therapeutic Areas', icon: 'pulse' },
  { key: 'molecule-developments', path: '/molecule-developments', label: 'Molecules', icon: 'atom' },
  { key: 'pharmaceutical-form-developments', path: '/pharmaceutical-form-developments', label: 'Pharmaceutical Forms', icon: 'pill' },
  { key: 'supplier-portfolio-evolution', path: '/supplier-portfolio-evolution', label: 'Portfolio Evolution', icon: 'trendingUp' },
  { key: 'predictive-supplier-developments', path: '/predictive-supplier-developments', label: 'Predictive Analysis', icon: 'sparkles' },
  { key: 'nl-market-gap-analysis', path: '/nl-market-gap-analysis', label: 'NL Market Gaps', icon: 'target' },
  { key: 'my-favorite-suppliers', path: '/my-favorite-suppliers', label: 'My Favorite Suppliers', icon: 'stars' },
];

const EXTERNAL_CHILDREN = [
  { key: 'ema-supps', path: '/ema-supps', label: 'EMA Register', icon: 'stars' },
  { key: 'dutch-supps', path: '/dutch-supps', label: 'Dutch CBG-MEB', icon: 'stars' },
  { key: 'ansm-supps', path: '/ansm-supps', label: 'ANSM France', icon: 'stars' },
  { key: 'hma-supps', path: '/hma-supps', label: 'HMA (MRI)', icon: 'stars' },
];

const ALL_SUPPLIER_PATHS = [
  '/suppliers', '/supplier-products', '/supplier-contracts', '/delivery-delays', '/file-monitoring',
  '/therapeutic-area-developments', '/molecule-developments', '/pharmaceutical-form-developments',
  '/supplier-portfolio-evolution', '/predictive-supplier-developments', '/nl-market-gap-analysis', '/my-favorite-suppliers',
  '/ema-supps', '/dutch-supps', '/ansm-supps', '/hma-supps',
];

const MI_CHILDREN = [
  { key: 'mach-data', path: '/market-intelligence/mach', label: 'MACH Data', icon: 'boxes', expandable: true },
  { key: 'mi-red-list', path: '/market-intelligence/red-list', label: 'Red List', icon: 'target' },
  { key: 'mi-orange-list', path: '/market-intelligence/orange-list', label: 'Orange List', icon: 'sparkles' },
  { key: 'mi-cbg', path: '/market-intelligence/cbg', label: 'CBG', icon: 'atom' },
  { key: 'mi-nhg', path: '/market-intelligence/nhg', label: 'NHG', icon: 'pulse' },
  { key: 'mi-fk', path: '/market-intelligence/fk', label: 'FK', icon: 'compass' },
];

const ALL_MI_PATHS = ['/market-intelligence/mach', '/market-intelligence/retail-hosp', '/market-intelligence/red-list', '/market-intelligence/orange-list', '/market-intelligence/cbg', '/market-intelligence/nhg', '/market-intelligence/fk'];

const SYSTEM_CHILDREN = [
  { key: 'activity-log', path: '/activity-log', label: 'Activity Log', icon: 'clockHistory' },
];

const ALL_SYSTEM_PATHS = ['/activity-log'];

const SUPPORT_CHILDREN = [
  { key: 'report-incident', path: '/support/report-incident', label: 'Report Incident', icon: 'clockHistory' },
];

const ALL_SUPPORT_PATHS = ['/support/report-incident'];

const BOTTOM_ITEMS = [];

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const { instance } = useMsal();
  const { displayName, email, initials } = useUser();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [todoCount, setTodoCount] = useState(0);
  const todoTimer = useRef(null);

  // Fetch to-do count, poll every 60s
  useEffect(() => {
    const fetchTodos = async () => {
      try {
        const auth = await getAuthHeaders(instance);
        const r = await fetch(`${TODO_API}/api/profile/todos`, { headers: auth });
        if (!r.ok) return;
        const d = await r.json();
        setTodoCount(d.total_count || 0);
      } catch {}
    };
    fetchTodos();
    todoTimer.current = setInterval(fetchTodos, 60000);
    return () => clearInterval(todoTimer.current);
  }, [instance]);

  // Refresh when navigating to /supplier-products (user may have resolved a to-do)
  useEffect(() => {
    if (location.pathname === '/supplier-products') {
      const t = setTimeout(async () => {
        try {
          const auth = await getAuthHeaders(instance);
          const r = await fetch(`${TODO_API}/api/profile/todos`, { headers: auth });
          if (!r.ok) return;
          const d = await r.json();
          setTodoCount(d.total_count || 0);
        } catch {}
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [location.pathname, instance]);

  const isChildActive = ALL_SUPPLIER_PATHS.some(p =>
    p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)
  );
  const isSystemActive = ALL_SYSTEM_PATHS.some(p => location.pathname.startsWith(p));
  const isSupportActive = ALL_SUPPORT_PATHS.some(p => location.pathname.startsWith(p));
  const isMiActive = ALL_MI_PATHS.some(p => location.pathname.startsWith(p));

  const [expanded, setExpanded] = useState(isChildActive);
  const [sysExpanded, setSysExpanded] = useState(isSystemActive);
  const [supExpanded, setSupExpanded] = useState(isSupportActive);
  const [miExpanded, setMiExpanded] = useState(isMiActive);
  const [manualSupplier, setManualSupplier] = useState(false);
  const [manualSystem, setManualSystem] = useState(false);
  const [manualSupport, setManualSupport] = useState(false);
  const [manualMi, setManualMi] = useState(false);

  // Auto-expand/collapse on route change (reset manual overrides)
  useEffect(() => {
    setExpanded(isChildActive);
    setSysExpanded(isSystemActive);
    setSupExpanded(isSupportActive);
    setMiExpanded(isMiActive);
    setManualSupplier(false);
    setManualSystem(false);
    setManualSupport(false);
    setManualMi(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleExpanded = () => {
    setManualSupplier(true);
    setExpanded(prev => !prev);
  };
  const toggleSysExpanded = () => {
    setManualSystem(true);
    setSysExpanded(prev => !prev);
  };
  const toggleSupExpanded = () => {
    setManualSupport(true);
    setSupExpanded(prev => !prev);
  };
  const toggleMiExpanded = () => {
    setManualMi(true);
    setMiExpanded(prev => !prev);
  };

  // Dynamic retail_hosp year sub-items under MACH Data
  const [machYears, setMachYears] = useState([]);
  const isMachYearActive = location.pathname.startsWith('/market-intelligence/retail-hosp');
  const [machSubOpen, setMachSubOpen] = useState(isMachYearActive);

  useEffect(() => {
    (async () => {
      try {
        const auth = await getAuthHeaders(instance);
        const res = await fetch(`${MI_API}/api/market-intelligence/retail-hosp/years`, { headers: auth });
        if (res.ok) {
          const yrs = await res.json();
          if (Array.isArray(yrs)) setMachYears(yrs);
        }
      } catch { /* ignore — shows MACH Data with no children */ }
    })();
  }, [instance]);

  // Auto-expand MACH sub-items when on a year page
  useEffect(() => {
    if (isMachYearActive) setMachSubOpen(true);
  }, [isMachYearActive]);

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.sb-user-wrap')) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      {/* Mobile backdrop */}
      {!collapsed && (
        <div className="sb-backdrop d-lg-none" onClick={onToggle} />
      )}

      <nav className={`sb d-flex flex-column${collapsed ? ' sb-collapsed' : ''}`}>
        {/* ── Brand ── */}
        <NavLink to="/" className="sb-brand text-decoration-none" onClick={() => window.innerWidth < 992 && onToggle?.()}>
          <div className="sb-logo d-flex align-items-center gap-2">
            <img src="/images/logo.png" alt="FocusOne" height="45" style={{ objectFit: "contain" }} />
          </div>
        </NavLink>

        {/* ── Main nav ── */}
        <div className="sb-nav flex-grow-1 overflow-auto">
          {/* Overview */}
          <div>
            <div className="sb-sec">{OVERVIEW_SECTION.label}</div>
            {OVERVIEW_SECTION.items.map((item) => (
              <SidebarItem key={item.key} item={item} onNavigate={() => window.innerWidth < 992 && onToggle?.()} />
            ))}
          </div>

          {/* Supplier Module — expandable */}
          <div style={{ marginTop: 12 }}>
            {/* Parent row styled like a nav item (si class) */}
            <div
              className={`si${isChildActive ? ' active' : ''}`}
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={toggleExpanded}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {icons.boxes}
                Supplier
              </span>
              <i className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'}`} style={{ fontSize: 10, opacity: 0.5 }} />
            </div>

            {expanded && (
              <div style={{ paddingLeft: 28 }}>
                <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 0 8px 0' }} />
                {SUPPLIER_CHILDREN.map((item) => (
                  <SidebarItem key={item.key} item={item} onNavigate={() => window.innerWidth < 992 && onToggle?.()} />
                ))}

                <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 0 8px 0' }} />
                {/* Data Analysis subsection */}
                <div className="sb-sec" style={{ fontSize: 10, opacity: 0.5, marginTop: 4, marginBottom: 2 }}>
                  Data Analysis
                </div>
                {ANALYTICS_CHILDREN.map((item) => (
                  <SidebarItem key={item.key} item={item} onNavigate={() => window.innerWidth < 992 && onToggle?.()} />
                ))}

                <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 0 8px 0' }} />
                {/* External Data subsection */}
                <div className="sb-sec" style={{ fontSize: 10, opacity: 0.5, marginTop: 4, marginBottom: 2 }}>
                  External Data
                </div>
                {EXTERNAL_CHILDREN.map((item) => (
                  <SidebarItem key={item.key} item={item} onNavigate={() => window.innerWidth < 992 && onToggle?.()} />
                ))}
              </div>
            )}
          </div>

          {/* Market Intelligence — expandable */}
          <div style={{ marginTop: 12 }}>
            <div
              className={`si${isMiActive ? ' active' : ''}`}
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={toggleMiExpanded}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {icons.trendingUp}
                Market Intelligence
              </span>
              <i className={`bi ${miExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`} style={{ fontSize: 10, opacity: 0.5 }} />
            </div>

            {miExpanded && (
              <div style={{ paddingLeft: 28 }}>
                <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 0 8px 0' }} />

                {/* MACH Data — second-level expandable with year sub-items */}
                <div
                  className={`si${location.pathname.startsWith('/market-intelligence/mach') || isMachYearActive ? ' active' : ''}`}
                  style={{ cursor: 'pointer', justifyContent: 'space-between' }}
                  onClick={() => setMachSubOpen(o => !o)}
                >
                  <NavLink
                    to="/market-intelligence/mach"
                    end
                    onClick={(e) => { e.stopPropagation(); if (window.innerWidth < 992) onToggle?.(); }}
                    style={{ display: 'contents', color: 'inherit', textDecoration: 'none' }}
                  >
                    {icons.boxes}
                    MACH Data
                  </NavLink>
                  {machYears.length > 0 && (
                    <i className={`bi ${machSubOpen ? 'bi-chevron-down' : 'bi-chevron-right'}`} style={{ fontSize: 10, opacity: 0.5, marginLeft: 'auto' }} />
                  )}
                </div>

                {machSubOpen && machYears.length > 0 && (
                  <div style={{ paddingLeft: 26 }}>
                    {machYears.map(yr => (
                      <NavLink
                        key={yr}
                        to={`/market-intelligence/retail-hosp/${yr}`}
                        className={({ isActive }) => `si${isActive ? ' active' : ''}`}
                        onClick={() => window.innerWidth < 992 && onToggle?.()}
                        style={{ fontSize: 12 }}
                      >
                        {icons.products}
                        {yr} Data
                      </NavLink>
                    ))}
                  </div>
                )}

                {/* CBG, NHG */}
                {MI_CHILDREN.filter(item => !item.expandable).map((item) => (
                  <SidebarItem key={item.key} item={item} onNavigate={() => window.innerWidth < 992 && onToggle?.()} />
                ))}
              </div>
            )}
          </div>

          {/* System Management — expandable */}
          <div style={{ marginTop: 12 }}>
            <div
              className={`si${isSystemActive ? ' active' : ''}`}
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={toggleSysExpanded}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {icons.settings}
                System Management
              </span>
              <i className={`bi ${sysExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`} style={{ fontSize: 10, opacity: 0.5 }} />
            </div>

            {sysExpanded && (
              <div style={{ paddingLeft: 28 }}>
                <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 0 8px 0' }} />
                {SYSTEM_CHILDREN.map((item) => (
                  <SidebarItem key={item.key} item={item} onNavigate={() => window.innerWidth < 992 && onToggle?.()} />
                ))}
              </div>
            )}
          </div>

          {/* Support — expandable */}
          <div style={{ marginTop: 12 }}>
            <div
              className={`si${isSupportActive ? ' active' : ''}`}
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={toggleSupExpanded}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {icons.support}
                Support
              </span>
              <i className={`bi ${supExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`} style={{ fontSize: 10, opacity: 0.5 }} />
            </div>

            {supExpanded && (
              <div style={{ paddingLeft: 28 }}>
                <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 0 8px 0' }} />
                {SUPPORT_CHILDREN.map((item) => (
                  <SidebarItem key={item.key} item={item} onNavigate={() => window.innerWidth < 992 && onToggle?.()} />
                ))}
              </div>
            )}
          </div>

          {BOTTOM_ITEMS.length > 0 && (
            <>
              <div className="sb-sep" />
              {BOTTOM_ITEMS.map((item) => (
                <SidebarItem key={item.key} item={item} onNavigate={() => window.innerWidth < 992 && onToggle?.()} />
              ))}
            </>
          )}
        </div>

        {/* ── User profile with dropdown ── */}
        <div className="sb-user-wrap" style={{ position: 'relative' }}>
          {showUserMenu && (
            <div style={{
              position: 'absolute', bottom: '100%', left: 0, right: 0,
              background: '#1e4f6b', borderRadius: '8px 8px 0 0',
              border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', zIndex: 999
            }}>
              <NavLink
                to="/profile"
                onClick={() => { setShowUserMenu(false); if (window.innerWidth < 992) onToggle?.(); }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 16px', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontSize: '12.5px' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                </svg>
                My Profile
              </NavLink>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)' }} />
              <button
                onClick={() => { setShowUserMenu(false); instance.logoutRedirect(); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 16px', color: 'rgba(255,255,255,0.75)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12.5px', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(214,59,10,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </div>
          )}

          <div
            className="sb-user"
            onClick={() => setShowUserMenu(prev => !prev)}
            style={{ cursor: 'pointer', background: showUserMenu ? 'rgba(255,255,255,0.07)' : '' }}
          >
            <div className="sb-av">{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div className="sb-uname" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{displayName}</div>
                {todoCount > 0 && <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>({todoCount})</span>}
              </div>
              <div className="sb-urole" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>
            </div>
            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.3)" strokeWidth="2" style={{ transform: showUserMenu ? 'rotate(180deg)' : '', transition: 'transform 0.2s', flexShrink: 0 }}>
              <path d="M5 15l7-7 7 7" />
            </svg>
          </div>
        </div>
      </nav>
    </>
  );
}

function SidebarItem({ item, onNavigate }) {
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) => `si${isActive ? ' active' : ''}`}
      onClick={onNavigate}
    >
      {icons[item.icon]}
      {item.label}
      {item.badge && (
        <span className={`sb-bdg${item.badgeClass ? ` ${item.badgeClass}` : ''}`}>
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}
